import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./AnimatedText.css";

interface SimpleRotatingTextProps {
  texts: string[];
  interval?: number;
  className?: string;
  prefixText?: string;
  textSize?: "sm" | "md" | "lg" | "xl";
}

const SimpleRotatingText: React.FC<SimpleRotatingTextProps> = ({
  texts,
  interval = 4000,
  className = "",
  prefixText = "AI-NATIVE",
  textSize = "xl",
}) => {
  const [index, setIndex] = useState(0);
  const [key, setKey] = useState(0);
  const [prefixFontSize, setPrefixFontSize] = useState("72px");
  const [rotatingFontSize, setRotatingFontSize] = useState("64px");
  const [isMobile, setIsMobile] = useState(false);
  const [lineHeight, setLineHeight] = useState("1.2");
  const [containerHeight, setContainerHeight] = useState("1.2em");

  // Ensure we're working with a clean array of texts
  const uniqueTexts = Array.from(new Set(texts));

  // Handle responsive font sizes
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);

      if (window.innerWidth <= 480) {
        setPrefixFontSize("24px");
        setRotatingFontSize("20px");
        setLineHeight("1.1");
        setContainerHeight("1.1em");
      } else if (window.innerWidth <= 768) {
        setPrefixFontSize("24px");
        setRotatingFontSize("20px");
        setLineHeight("1.1");
        setContainerHeight("1.1em");
      } else if (window.innerWidth <= 1200) {
        setPrefixFontSize("60px");
        setRotatingFontSize("54px");
        setLineHeight("1.2");
        setContainerHeight("1.2em");
      } else {
        setPrefixFontSize("72px");
        setRotatingFontSize("64px");
        setLineHeight("1.2");
        setContainerHeight("1.2em");
      }
    };

    handleResize(); // Set initial size
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    // Reset if texts array changes
    setIndex(0);
    setKey((prev) => prev + 1);
  }, [texts]);

  useEffect(() => {
    if (uniqueTexts.length <= 1) return;

    const timer = setInterval(() => {
      setIndex((prevIndex) => {
        const nextIndex = (prevIndex + 1) % uniqueTexts.length;
        setKey((prev) => prev + 1);
        return nextIndex;
      });
    }, interval);

    return () => clearInterval(timer);
  }, [uniqueTexts, interval]);

  const containerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    textAlign: "left",
    width: "100%",
  };

  const prefixStyle: React.CSSProperties = {
    display: "block",
    lineHeight: lineHeight,
    marginBottom: isMobile ? "0.2rem" : "0.5rem",
    color: "white",
    textTransform: "uppercase",
    fontWeight: "bold",
    fontSize: prefixFontSize,
    textAlign: "left",
  };

  const rotatingContainerStyle: React.CSSProperties = {
    display: "block",
    position: "relative",
    lineHeight: lineHeight,
    height: containerHeight,
    color: "white",
    textTransform: "uppercase",
    fontWeight: "bold",
    width: "100%",
    overflow: "visible",
    fontSize: rotatingFontSize,
    textAlign: "left",
  };

  return (
    <div style={containerStyle}>
      <div style={prefixStyle}>{prefixText}</div>
      <div style={rotatingContainerStyle}>
        <AnimatePresence mode="wait">
          <motion.div
            key={key}
            initial={{ opacity: 0, y: isMobile ? 10 : 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: isMobile ? -10 : -20 }}
            transition={{
              duration: isMobile ? 0.3 : 0.5,
              ease: "easeInOut",
            }}
            style={{
              position: "relative",
              display: "inline-block",
              width: "100%",
              textAlign: "left",
            }}
          >
            {uniqueTexts[index]}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SimpleRotatingText;
